<template>
  <div>
    <div class="payments__filters">
      <CustomDateRange 
        label="Период"
        :value="filters.date_range" 
        @change_range="(value) => onChange('date_range', value)"
      />
  
      <CustomSelect
        :options="this.statuses"
        label="Статус"
        optionValueKey="status_code"
        optionLabelKey="status_name"
        style="width: 220px;"
        :value="this.filters.status_code"
        @change="(value, row) => onChange('status_code', value, row)"
      />
      <CustomCheckbox
        :value="!!docsForSignOnly"
        @change="(value) => onDocsForSignOnlyChange(value)"
        label="Документы на подпись"
        :small="true"
      />
      <div v-if="isPaymentNoticePeriod && !isPaymentNoticeVisible" class="payments__filters-info">
        <q-btn
          flat
          round
          dense
          icon="info"
          color="amber-9"
          @click="openPaymentNotice"
        />
      </div>
    </div>

    <q-banner
      v-if="isPaymentNoticePeriod && isPaymentNoticeVisible"
      inline-actions
      class="payment-notice-banner tw-mb-3 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-text-amber-900"
    >
      Обращаем внимание, что в соответствии с внутренним регламентом компании первые дни месяца являются неплатежными.
      <template #action>
        <q-btn flat round dense icon="close" color="amber-9" @click="closePaymentNotice" />
      </template>
    </q-banner>

    <div v-if="payInfo" class="tw-mt-4 tw-mb-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
      <div class="tw-flex tw-flex-wrap tw-gap-3 tw-items-center tw-justify-between">
        <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <div class="tw-text-sm tw-text-slate-500">Договор:</div>
            <div class="tw-text-sm tw-font-medium tw-text-slate-800">
              {{ agreementInfo?.agreement_num || '—' }}
            </div>
          </div>
          <div class="tw-text-sm tw-text-slate-500">Общая сумма:</div>
          <div class="tw-text-sm tw-font-medium tw-text-slate-800">
            <Amount :value="payInfoAgreementSumWithoutWarranty" />
          </div>

          <div class="tw-cursor-pointer tw-flex tw-items-center">
            <SvgIcon name="info" size="14px" class="tw-text-primary-light" />
            <q-popup-proxy>
              <q-banner class="tw-p-2">
                <div class="tw-text-sm tw-text-slate-500 tw-mb-1">Сумма договора с гарантией</div>
                <div class="tw-font-semibold">
                  <Amount :value="payInfoAgreementSum" round="2" />
                </div>
              </q-banner>
            </q-popup-proxy>
          </div>
        </div>
      </div>

      <div class="tw-mt-2">
        <div class="pay-info-bar" :title="`Оплачено по акту сверки: ${payInfoPaidPercent}%`">
          <div v-if="payInfoPaidRatio > 0" class="pay-info-bar__paid" :style="payInfoPaidBarStyle" />
          <div v-if="payInfoRemainingRatio > 0" class="pay-info-bar__remaining" :style="payInfoRemainingBarStyle" />
        </div>

        <div class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-3 tw-items-end tw-justify-between">
          <div class="tw-flex tw-items-center tw-gap-4 tw-flex-wrap">
            <div class="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
              <div class="tw-text-sm tw-text-slate-500">Оплаченная сумма по акту сверки:</div>
              <div class="tw-text-sm tw-font-medium green">
                <Amount :value="payInfoPayedSum" round="2" />
              </div>
            </div>
            <div class="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
              <div class="tw-text-sm tw-text-slate-500">Ожидаемая оплата:</div>
              <div class="tw-text-sm tw-font-medium" :class="payInfoExpectedPayment > 0 ? 'tw-text-amber-700' : 'green'">
                <Amount :value="payInfoExpectedPayment" round="2" />
              </div>
              <div v-if="payInfoExpectedPaymentWithWarranty > 0" class="tw-cursor-pointer tw-flex tw-items-center">
                <SvgIcon name="info" size="14px" class="tw-text-primary-light" />
                <q-popup-proxy>
                  <q-banner class="tw-p-2">
                    <div class="tw-text-sm tw-text-slate-500 tw-mb-1">Сумма с гарантией</div>
                    <div class="tw-font-semibold">
                      <Amount :value="payInfoExpectedPaymentWithWarranty" round="2" />
                    </div>
                  </q-banner>
                </q-popup-proxy>
              </div>
            </div>
          </div>

          <div class="tw-text-right tw-flex tw-flex-col tw-items-end tw-gap-1">
            <div class="tw-flex tw-items-center tw-gap-1 tw-flex-wrap tw-justify-end">
              <div class="tw-text-sm tw-text-slate-500">Оставшаяся сумма:</div>
              <div class="tw-text-sm tw-font-medium tw-text-red-600">
                <Amount :value="payInfoRemainingSum" round="2" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="tw-flex tw-items-center tw-justify-between tw-mt-2">
      <CustomTabs
        :data="tabs"
        :tab="activeTab"
        @change_tab="onTabChange"
        :alt="true"
        :paddingless="true"
      />
      <div class="tw-flex tw-items-center tw-justify-end">
        <q-btn
          v-if="docsForSignOnly"
          :loading="batchSigning"
          :disable="selectedPaymentIds.length === 0"
          size="sm"
          class="tw-mr-2" style="height: 32px; font-size: 12px; background-color: #0849C6; color: #fff;"
          @click="handleBatchSignPayments"
        >
          Подписать документы
        </q-btn>
        <q-btn
          :loading="isFetching"
          @click="getData"
          style="background-color: #0060FE; color: #fff; height: 32px; font-size: 12px;"
          size="xs" 
        >
          <q-icon name="replay" />
        </q-btn>
      </div>
    </div>

    <div v-if="activeTab === 'payments' || activeTab === 'errors'" style="margin-top: 15px;">
      <CustomTable
        :rowsData="paymentsTableRows"
        :columns="paymentsColumnsWithExpand"
        :stickyHeader="true"
        :stickyBottomRow="true"
        :loading="isFetching"
        :pageHeaderHeight="0"
        :custom-body="true"
        :bottomRow="true"
        row-key="finance_payment_id"
      >
        <template #customBody="props">
          <q-tr>
            <custom-table-cell v-if="docsForSignOnly" vertical-align="center" class="text-center">
              <CustomCheckbox
                :value="isPaymentSelected(props.row)"
                @change="(value) => togglePaymentSelect(props.row, value)"
              />
            </custom-table-cell>
            <custom-table-cell vertical-align="center" auto-width class="expand-cell tw-text-center">
              <q-btn
                v-if="(props.row.remont_costs || []).length > 0"
                size="sm"
                flat
                dense
                round
                :icon="expandedPaymentIds[props.row.finance_payment_id] ? 'expand_less' : 'expand_more'"
                @click="togglePaymentExpand(props.row.finance_payment_id)"
              />
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              {{ props.row.date_payment || (props.row?.is_payed_by_avance ? props.row.date_create : '—')}}
            </custom-table-cell>
            <custom-table-cell align="right" vertical-align="center">
              <div class="tw-text-sm tw-font-semibold">
                <Amount :value="props?.row?.is_payed_by_avance ? props.row.payment_amount : props.row.fp_payment_amount" round="2" />
              </div>
              <!-- <div v-if="props.row.reconciliation_sum" class="subtitle tw-mt-4 green">
                <span style="display: block; margin-bottom: 2px; color: #64748B; letter-spacing: 1px;">Оплачено:</span>
                <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
                  <Amount :value="props.row.reconciliation_sum" round="2" />
                  <div class="tw-cursor-pointer tw-flex tw-items-center">
                    <SvgIcon name="info" size="14px" class="green" />
                    <q-popup-proxy>
                      <q-banner class="tw-p-2">
                        <div class="tw-text-sm tw-text-slate-500 tw-mb-1">Оплачено по акту сверки:</div>
                        <Amount :value="props.row.reconciliation_sum" round="2" />
                      </q-banner>
                    </q-popup-proxy>
                  </div>
                </div>
              </div> -->
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
              <PaymentStatusStepper
                v-if="props.row.current_status"
                :statusCode="props.row.current_status"
                :errorMessage="props.row.status_desc"
                :errorTitle="props.row.current_status_name"
                :errorCode="props.row.create_error_code"
                :isPaymentExpired="props.row.is_payment_expired"
                :payInfo="payInfo ? {...payInfo, payment_amount: props.row.fp_payment_amount} : {}"
                :addInfo="(props.row.create_error_code === 'ERR_DOCS_NOT_SIGNED' || props.row?.isGroup) ? props.row.documents : props.row.error_bi"
                :errors="props.row?.create_error || []"
                @sign-document="onSignDocument"
                :signing="this.signing"
              />
              <span v-else>—</span>
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
              {{props.row.responsible || ''}}
            </custom-table-cell>
          </q-tr>
          <q-tr v-if="expandedPaymentIds[props.row.finance_payment_id] && (props.row.remont_costs || []).length">
            <custom-table-cell vertical-align="center" :colspan="paymentsColumnsWithExpand.length" class="tw-bg-grey-2 tw-p-3">
              <div class="finance-payment-nested">
                <div class="tw-text-sm tw-font-medium tw-mb-2">Вложенные платежи</div>
                <table class="nested-table">
                  <thead>
                    <tr v-if="!props.row.isGroup">
                      <th class="text-center">ID</th>
                      <th class="text-center">Дата создания</th>
                      <th class="text-center">Блок</th>
                      <th class="text-center">Подъезд</th>
                      <th class="text-center">Этаж</th>
                      <th class="text-left">Группа работ</th>
                      <th class="text-right">Сумма платежа</th>
                      <th v-if="!props.row.isPaidByAvance" class="text-right">Сумма платежа по акту сверки</th>
                    </tr>
                    <tr v-else>
                      <th class="text-center">ID</th>
                      <th class="text-center">Дата создания</th>
                      <th class="text-center">Блок</th>
                      <th class="text-center">Подъезд</th>
                      <th class="text-center">Этаж</th>
                      <th class="text-left">Группа работ</th>
                      <th class="text-right">Сумма платежа</th>
                      <th class="text-center">Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-if="!props.row.isGroup"
                      v-for="rc in props.row.remont_costs"
                      :key="rc.remont_costs_id"
                    >
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.remont_costs_id }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.date_create }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.block_name || '—' }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.entrance || '—' }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.floor }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-left">{{ rc.work_set_check_group_name }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-right"><Amount :value="rc.payment_amount" round="2" /></custom-table-cell>
                      <custom-table-cell v-if="!props.row.isPaidByAvance" vertical-align="center" class="text-right"><Amount :value="rc.pay_request_amount" round="2" /></custom-table-cell>
                    </tr>

                    <tr
                      v-else
                      v-for="rc in props.row.remont_costs"
                      :key="rc.remont_costs_id + 'group'"
                    >
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.remont_costs_id }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.date_create }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.block_name || '—' }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.entrance || '—' }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center">{{ rc.floor }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-left">{{ rc.work_set_check_group_name }}</custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-right"><Amount :value="rc.payment_amount" round="2" /></custom-table-cell>
                      <custom-table-cell vertical-align="center" class="text-center nested-status-cell">
                        <template v-if="rc.current_status">
                          <div class="nested-status-inner">
                            <div class="tw-cursor-pointer tw-flex tw-items-center tw-gap-2">
                              <span class="nested-status-text">{{ rc.current_status_name }}</span>
                              <template v-if="isNestedRowError(rc)">
                                <SvgIcon name="info" size="14px" class="tw-text-red-600" />
                              </template>
                            </div>
                            <div v-if="getNestedErrorDisplay(rc)" class="nested-status-subtitle">
                              <div v-if="rc?.create_error?.length">
                                <div v-for="item in rc?.create_error" :key="item.error_code">
                                  <div :key="item.error_code" class="tw-flex tw-items-center tw-gap-2">
                                    {{ item?.error_desc }}
                                    <div class="tw-cursor-pointer" v-if="item?.error_code === 'CREATE_ERROR_EXCEED' || item?.error_code === 'ERR_DOCS_NOT_SIGNED' || item?.documents?.length">
                                      <SvgIcon name="info" size="14px" class="tw-text-red-600"  />
                                      <q-popup-proxy>
                                        <q-banner class="tw-p-2">
                                          <template v-if="item?.error_code === 'CREATE_ERROR_EXCEED' && payInfo">
                                            <div class="tw-font-medium tw-text-slate-800 tw-mb-2">
                                              Сумма платежа превышает Сумму договора
                                            </div>
                                            <div class="nested-exceed-block tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
                                              <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                                                <div class="tw-text-sm tw-text-slate-500">Cумма всех платежей:</div>
                                                <div class="tw-text-sm tw-font-medium">
                                                  <Amount :value="getNestedExceedPayAmount(payInfo)" />
                                                </div>
                                              </div>
                                              <div class="nested-pay-info-bar">
                                                <div v-if="getNestedExceedRatios(payInfo).agreement > 0" class="nested-pay-info-bar__paid" :style="{ width: getNestedExceedRatios(payInfo).agreement * 100 + '%' }" />
                                                <div v-if="getNestedExceedRatios(payInfo).overage > 0" class="nested-pay-info-bar__remaining" :style="{ width: getNestedExceedRatios(payInfo).overage * 100 + '%' }" />
                                              </div>
                                              <div class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-3 tw-items-end tw-justify-between">
                                                <div class="tw-flex tw-items-center tw-gap-1">
                                                  <span class="tw-text-sm tw-text-slate-500">Сумма договора:</span>
                                                  <span class="tw-text-sm tw-font-medium green">
                                                    <Amount :value="getNestedExceedSums(payInfo).agreementSum" />
                                                  </span>
                                                </div>
                                                <div class="tw-flex tw-items-center tw-gap-1">
                                                  <span class="tw-text-sm tw-text-slate-500">Превышения:</span>
                                                  <span class="tw-text-sm tw-font-medium tw-text-red-600">
                                                    <Amount :value="getNestedExceedSums(payInfo).overageSum" />
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                            <div class="tw-text-sm tw-text-slate-500 tw-mt-2">
                                              Необходимо создать ДС
                                            </div>
                                          </template>
                                          <template v-else-if="item?.error_code === 'ERR_DOCS_NOT_SIGNED' && getNestedDocs(rc)?.length">
                                            <div class="tw-text-xs tw-text-slate-800">
                                              Подпишите документы, чтобы разблокировать оплату:
                                            </div>
                                            <div class="nested-docs-divider" />
                                            <div class="tw-flex tw-flex-col tw-gap-2">
                                              <div
                                                v-for="doc in getNestedDocs(rc)"
                                                :key="doc.floor_map_document_id"
                                                class="nested-docs-row"
                                              >
                                                <div class="tw-flex tw-items-center tw-gap-2">
                                                  <SvgIcon name="document_pen" size="16px" class="tw-text-[#0849C6]" color="#0849C6" />
                                                  <span class="tw-text-xs tw-text-slate-800">{{ doc.floor_map_document_type_name }}</span>
                                                  <q-icon
                                                    v-if="doc.assign_signs?.length"
                                                    name="info"
                                                    size="16px"
                                                    class="tw-cursor-pointer tw-text-slate-500"
                                                    @click.stop
                                                  >
                                                    <q-popup-proxy>
                                                      <q-banner class="tw-p-3 tw-min-w-[280px]">
                                                        <div class="tw-text-sm tw-font-medium tw-text-slate-800 tw-mb-2">Подписанты</div>
                                                        <div class="tw-flex tw-flex-col tw-gap-2">
                                                          <div
                                                            v-for="signer in doc.assign_signs"
                                                            :key="signer.assign_type_id"
                                                            class="tw-text-xs tw-border-b tw-border-slate-100 tw-pb-2 tw-last:border-0 tw-last:pb-0"
                                                          >
                                                            <div class="tw-font-medium tw-text-slate-800">{{ signer.assign_type_name }}</div>
                                                            <div class="tw-text-slate-600">{{ signer.employee_fio }}</div>
                                                            <div class="tw-mt-1" :class="signer.is_signed ? 'green' : 'tw-text-amber-600'">
                                                              {{ signer.is_signed ? `Подписан ${signer.sign_date || ''}` : 'Ожидает подписи' }}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      </q-banner>
                                                    </q-popup-proxy>
                                                  </q-icon>
                                                </div>
                                                <div class="tw-flex tw-items-center tw-gap-1">
                                                  <div class="nested-docs-status-icon">
                                                    <q-icon name="schedule" size="14px" color="white" />
                                                  </div>
                                                  <span class="tw-text-xs tw-text-slate-800">{{ doc.can_sign && !hasCurrentUserSigned(doc) ? "Ожидает вашей подписи" : "Ожидание подписантов" }}</span>
                                                </div>
                                                <template v-if="hasCurrentUserSigned(doc)">
                                                  <span class="tw-text-xs green tw-font-medium">Подписано</span>
                                                </template>
                                                <q-btn
                                                  v-else-if="doc.can_sign"
                                                  unelevated
                                                  no-caps
                                                  style="background-color: #0849C6; color: white;"
                                                  size="sm"
                                                  class="tw-ml-auto tw-rounded-lg"
                                                  :loading="getSigningLoading(doc)"
                                                  @click="onSignDocument(doc, rc)"
                                                >
                                                  Подписать
                                                </q-btn>
                                              </div>
                                            </div>
                                          </template>
                                          <template v-else-if="item?.documents?.length">
                                            <div class="tw-text-sm tw-text-slate-800 tw-font-semibold tw-px-2 tw-pb-2" v-if="item?.error_desc">
                                              {{ item?.error_desc || '' }}:
                                            </div>
                                            <div
                                              v-for="doc in getUniqueDocuments(item?.documents)"
                                              :key="doc.floor_map_document_id || doc.avr_number"
                                              class="tw-text-sm tw-text-slate-800 tw-px-2 tw-py-1"
                                            >
                                              {{ `АВР №${doc.avr_number}, ID документа: ${doc.floor_map_document_id}` }}
                                            </div>
                                          </template>
                                          <template v-else-if="getNestedErrorDisplay(rc)">
                                            <div class="nested-error-bi-text">{{ getNestedErrorDisplay(rc) }}</div>
                                          </template>
                                        </q-banner>
                                      </q-popup-proxy>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div v-else>
                                <div>{{ getNestedErrorDisplay(rc) }}</div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <span v-else>—</span>
                      </custom-table-cell>
                    </tr>
                  </tbody>
                </table>
              </div>
            </custom-table-cell>
          </q-tr>
        </template>
        <template #bottomRow>
          <q-tr v-if="this.paymentsTableRows?.length" class="sticky-bottom-row">
            <custom-table-cell vertical-align="center" auto-width class="expand-cell">
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <span class="tw-font-semibold">Итого</span>
            </custom-table-cell>
            <custom-table-cell align="right" vertical-align="center">
              <div class="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-font-semibold">
                <Amount :value="getPaymentsTotalAmount()" round="2" />
              </div>
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
            </custom-table-cell>
            <custom-table-cell v-if="docsForSignOnly" align="left" vertical-align="center">
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
    </div>
  </div>
</template>

<script>

import Amount from "@/components/common/Amount.vue";
import CheckOrClose from "@/components/common/CheckOrClose";
import Clickable from "@/components/common/Clickable";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import ShinyButton from "@/components/common/ShinyButton";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import CustomSelect from "@/components/form/CustomSelect";
import CustomDateRange from "@/components/common/CustomDateRange";
import CustomTabs from "@/components/common/CustomTabs";
import PaymentStatusStepper from "@/components/common/PaymentStatusStepper";
import {
  getEntrancePayments,
  signEntranceDocument,
  batchCreateDocuments,
} from "./services";
import { isChrome } from "@/services";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import SvgIcon from '@/components/common/SvgIcon';

export default {
  name: "ResidentialPayments",
  props: ['params', 'projectId'],
  components: {
    CustomTable,
    CustomTableCell,
    CustomCheckbox,
    Clickable,
    Amount,
    ShinyButton,
    CheckOrClose,
    CustomSelect,
    CustomButton,
    CustomDateRange,
    CustomTabs,
    PaymentStatusStepper,
    SvgIcon
  },
  data() {
    return {
      remontCosts: [],
      remontCostsPaidByAvance: [],
      payments: [],
      payInfo: null,
      expandedPaymentIds: {},
      activeTab: 'payments',
      loading: false,
      isFetching: false,
      downloading: {},
      agreementInfo: null,
      statuses: [],
      filters: { date_range: { from: '', to: '' }, floor: '', placement_type_id: '', project_entrance_id: 'ALL', entrance: '', status_code: '' },
      errorsCount: 0,
      signing: {},
      selectedPaymentIds: [],
      batchSigning: false,
      docsForSignOnly: false,
      isPaymentNoticeVisible: true,
    };
  },
  async mounted() {
    this.loadPaymentNoticeState();
    this.getData();
    this.loadErrorsCount();
  },
  computed: {
    isPaymentNoticePeriod() {
      const now = new Date();
      const day = now.getDate();
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      return day <= 4 || day >= lastDay - 1;
    },
    tabs() {
      const base = [
        { name: 'payments', label: 'Платежи' },
        {
          name: 'errors',
          label: 'Ошибки',
          badge: this.errorsCount > 0 ? this.errorsCount : undefined
        }
      ];
      return base;
    },
    paymentsColumns() {
      return [
        {
          name: 'date_payment',
          label: 'Дата создания',
          field: 'date_payment',
          align: 'center',
          sortable: true
        },
        {
          name: 'payment_amount',
          label: 'Сумма платежа',
          field: 'payment_amount',
          align: 'right',
          sortable: true
        },
        {
          name: 'finance_payment_status_name',
          label: 'Статус',
          field: 'finance_payment_status_name',
          align: 'left',
          sortable: true,
          style: 'min-width: 330px; width: 330px;'
        },
        {
          name: 'responsible',
          label: 'Ответственный',
          field: 'responsible',
          align: 'left',
          sortable: true
        }
      ]
    },
    paymentsTableRows() {
      if (!this.docsForSignOnly) return this.payments
      return this.payments.filter((p) =>
        (p.documents || []).some((doc) => this.canUserSignDocument(doc))
      )
    },
    paymentsColumnsWithExpand() {
      const expand = { name: '_expand', label: '', align: 'center', field: '_expand' }
      if (this.docsForSignOnly) {
        return [
          { name: '_select_payments', label: '', align: 'center', field: '_select_payments', style: 'width: 48px' },
          expand,
          ...this.paymentsColumns
        ]
      }
      return [expand, ...this.paymentsColumns]
    },
    payInfoAgreementSumWithoutWarranty() {
      return Math.round(this.payInfo?.agreement_sum_without_warranty ?? 0)
    },
    payInfoAgreementSum() {
      return this.payInfo?.agreement_sum ?? 0
    },
    payInfoPayedSum() {
      return this.payInfo?.payed_sum ?? 0
    },
    payInfoExpectedPayment() {
      return this.payInfo?.expected_payment_without_warranty ?? 0
    },
    payInfoExpectedPaymentWithWarranty() {
      return this.payInfo?.expected_payment_with_warranty ?? 0
    },
    payInfoUnpayedSum() {
      return this.payInfo?.unpayed_sum ?? 0
    },
    payInfoRemainingSum() {
      const total = Number(this.payInfoAgreementSumWithoutWarranty) || 0
      const paid = Number(this.payInfoPayedSum) || 0
      return Math.round(Math.max(0, total - paid))
    },
    payInfoOverageSum() {
      const unpayed = Number(this.payInfoUnpayedSum) || 0
      const remaining = Number(this.payInfoRemainingSum) || 0
      return Math.max(0, unpayed - remaining)
    },
    payInfoPaidPercent() {
      const total = Number(this.payInfoAgreementSumWithoutWarranty) || 0
      const paid = Number(this.payInfoPayedSum) || 0
      if (total <= 0) return 0
      return Math.round(Math.max(0, Math.min(1, paid / total)) * 100)
    },
    payInfoPaidRatio() {
      const total = Number(this.payInfoAgreementSumWithoutWarranty) || 0
      const paid = Number(this.payInfoPayedSum) || 0
      if (total <= 0) return 0
      return Math.max(0, Math.min(1, paid / total))
    },
    payInfoRemainingRatio() {
      const total = Number(this.payInfoAgreementSumWithoutWarranty) || 0
      const remaining = Number(this.payInfoRemainingSum) || 0
      if (total <= 0) return 0
      return Math.max(0, Math.min(1, remaining / total))
    },
    payInfoPaidBarStyle() {
      return { width: `${this.payInfoPaidRatio * 100}%` }
    },
    payInfoRemainingBarStyle() {
      return { width: `${this.payInfoRemainingRatio * 100}%` }
    }
  },
  methods: {
    getUniqueDocuments(documents) {
      if (!Array.isArray(documents)) return []
      const seen = new Set()
      return documents.filter((doc) => {
        const id = doc?.floor_map_document_id
        if (!id) return true
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
    },
    getPaymentsTotalAmount() {
      return (this.paymentsTableRows || []).reduce((sum, row) => {
        const value = row?.is_payed_by_avance ? row.payment_amount : row.fp_payment_amount
        if (typeof value === 'number') return sum + value
        const normalized = String(value ?? '').replace(/\s/g, '').replace(/,/g, '.')
        const parsed = Number(normalized)
        return sum + (Number.isFinite(parsed) ? parsed : 0)
      }, 0)
    },
    togglePaymentExpand(financePaymentId) {
      this.expandedPaymentIds = {
        ...this.expandedPaymentIds,
        [financePaymentId]: !this.expandedPaymentIds[financePaymentId]
      }
    },
    onTabChange(tab) {
      this.activeTab = tab
      this.getData()
    },
    async getData() {
      this.isFetching = true
      const statusCode = this.activeTab === 'errors' ? 'CREATE_ERROR' : (this.filters.status_code || undefined)
      const params = {
        ...this.params,
        project_id: this.projectId,
        floor: this.filters.floor || undefined,
        placement_type_id: this.filters.placement_type_id || undefined,
        project_entrance_id: this.filters.project_entrance_id === 'ALL' ? undefined : this.filters.project_entrance_id,
        date_from: this.filters.date_range?.from || undefined,
        date_to: this.filters.date_range?.to || undefined,
        status_code: statusCode
      }
      const res = await getEntrancePayments(params)
      this.isFetching = false
      if (!res) return
      this.remontCosts = res.remont_costs || []
      this.remontCostsPaidByAvance = res.remont_costs_paid_by_avance || []
      this.payments = res.payments || []
      this.statuses = res.statuses || []
      this.payInfo = res.pay_info || null
      this.agreementInfo = res?.agreement_info || null
      if (statusCode === 'CREATE_ERROR') {
        this.errorsCount = (res.payments || []).length
      }
    },
    async loadErrorsCount() {
      const params = {
        ...this.params,
        project_id: this.projectId,
        floor: this.filters.floor || undefined,
        placement_type_id: this.filters.placement_type_id || undefined,
        project_entrance_id: this.filters.project_entrance_id === 'ALL' ? undefined : this.filters.project_entrance_id,
        date_from: this.filters.date_range?.from || undefined,
        date_to: this.filters.date_range?.to || undefined,
        status_code: 'CREATE_ERROR'
      }
      const res = await getEntrancePayments(params)
      if (res) {
        this.errorsCount = (res.payments || []).length
      }
    },
    onChange(key, value, row) {
      this.filters[key] = value;
      if (key === 'project_entrance_id') {
        if (!value) {
          this.filters[key] = 'ALL'
          this.filters.entrance = ''
        } else {
          this.filters.entrance = row?.entrance || ''
        }
      }
      this.getData()
      this.loadErrorsCount()
    },
    onDocsForSignOnlyChange(value) {
      this.docsForSignOnly = value
      this.selectedPaymentIds = []
    },
    openPaymentNotice() {
      this.isPaymentNoticeVisible = true
    },
    closePaymentNotice() {
      this.isPaymentNoticeVisible = false
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('paymentsPaymentNoticeDismissed', '1')
        }
      } catch (e) {}
    },
    loadPaymentNoticeState() {
      try {
        if (typeof window === 'undefined' || !window.localStorage) return
        const dismissed = window.localStorage.getItem('paymentsPaymentNoticeDismissed')
        if (dismissed === '1') {
          this.isPaymentNoticeVisible = false
        }
      } catch (e) {
        // ignore storage errors
      }
    },
    canUserSignDocument(doc) {
      return doc?.assign_signs?.some((s) => s.can_sign && !s.is_signed) ?? false
    },
    isPaymentSelected(payment) {
      return this.selectedPaymentIds.includes(payment.finance_payment_id)
    },
    togglePaymentSelect(payment, value) {
      const id = payment.finance_payment_id
      if (value) {
        if (!this.selectedPaymentIds.includes(id)) {
          this.selectedPaymentIds = [...this.selectedPaymentIds, id]
        }
      } else {
        this.selectedPaymentIds = this.selectedPaymentIds.filter((x) => x !== id)
      }
    },
    async handleBatchSignPayments() {
      if (this.selectedPaymentIds.length === 0) {
        this.showErrorSnackbar('Не выбрано ни одного платежа')
        return
      }
      const selectedPayments = (this.paymentsTableRows || []).filter((p) =>
        this.selectedPaymentIds.includes(p.finance_payment_id)
      )
      const documentIds = []
      const seen = new Set()
      for (const payment of selectedPayments) {
        for (const doc of payment.documents || []) {
          if (!this.canUserSignDocument(doc) || !doc.floor_map_document_id) continue
          if (seen.has(doc.floor_map_document_id)) continue
          seen.add(doc.floor_map_document_id)
          documentIds.push(doc.floor_map_document_id)
        }
      }
      if (documentIds.length === 0) {
        this.showErrorSnackbar('Нет документов для подписания')
        return
      }
      if (documentIds.length > 10) {
        this.showErrorSnackbar('Максимальное количество документов для подписания — 10')
        return
      }
      this.batchSigning = true
      try {
        const signUrl = await batchCreateDocuments(documentIds, this.params)
        if (!signUrl) {
          this.showErrorSnackbar('Не получен URL для подписания')
          return
        }
        this.selectedPaymentIds = []
        if (isChrome()) {
          window.open(signUrl)
        } else {
          window.location.href = signUrl
        }
      } catch (e) {
        this.showErrorSnackbar(e?.response?.data?.message || e?.message || 'Ошибка при создании массовой подписи')
      } finally {
        this.batchSigning = false
      }
    },
    async onSignDocument(doc, row) {
      if (!doc?.floor_map_document_id) return
      const assignType = doc.assign_signs?.find((s) => s.can_sign && !s.is_signed)
      const assign_type_id = assignType?.assign_type_id
      const body = { floor_map_document_id: doc.floor_map_document_id }
      const key = doc.floor_map_document_id + '_' + (assign_type_id ?? '')
      this.signing[key] = true
      const res = await signEntranceDocument(body, this.params)
      this.signing[key] = false
      if (!res) return
      if (isChrome()) {
        window.open(res?.redirect_url)
        return
      }
      window.location.href = res?.redirect_url
    },
    getSigningLoading(doc) {
      const assignType = doc.assign_signs?.find((s) => s.can_sign && !s.is_signed)
      const key = doc.floor_map_document_id + '_' + (assignType?.assign_type_id ?? '')
      return !!this.signing[key]
    },
    hasCurrentUserSigned(doc) {
      const appStore = useAppStore()
      const employeeId = appStore.userData?.employee_id
      if (employeeId == null || !doc.assign_signs?.length) return false
      return doc.assign_signs.some((s) => s.employee_id === employeeId && s.is_signed)
    },
    isNestedRowError(rc) {
      return rc?.current_status?.toLowerCase?.()?.includes('error')
    },
    getNestedDocs(rc) {
      return rc?.documents || []
    },
    getNestedErrorBiText(rc) {
      const text = rc?.create_error_code === 'ERR_DOCS_NOT_SIGNED' ? null : rc?.error_bi
      return typeof text === 'string' ? text.trim() : ''
    },
    getNestedErrorDisplay(rc) {
      if (Array.isArray(rc?.create_error) && rc.create_error.length) {
        return rc.create_error.map((e) => e?.error_desc).filter(Boolean).join('\n')
      }
      if (rc?.status_desc) return rc.status_desc
      if (rc?.create_error && typeof rc.create_error === 'string') return rc.create_error
      return ''
    },
    getNestedExceedPayAmount(payInfo) {
      if (!payInfo) return 0
      const payed = Number(payInfo.payed_sum_sbs ?? payInfo.payed_sum) ?? 0
      const unpayed = Number(payInfo.unpayed_sum) ?? 0
      return payed + unpayed
    },
    getNestedExceedSums(payInfo) {
      const agreementSum = Number(Math.round(payInfo?.agreement_sum_without_warranty)) ?? 0
      const paid = Number(payInfo.payed_sum_sbs ?? payInfo.payed_sum) ?? 0
      const remaining = Math.max(0, agreementSum - paid)
      const unpayed = Number(payInfo?.unpayed_sum) ?? 0
      const overageSum = Math.max(0, unpayed - remaining)
      return { agreementSum, overageSum }
    },
    getNestedExceedRatios(payInfo) {
      const total = this.getNestedExceedPayAmount(payInfo)
      if (total <= 0) return { agreement: 0, overage: 0 }
      const agreementSum = Number(Math.round(payInfo?.agreement_sum_without_warranty)) ?? 0
      const paid = Number(payInfo?.payed_sum_sbs ?? payInfo?.payed_sum) ?? 0
      const remaining = Math.max(0, agreementSum - paid)
      const unpayed = Number(payInfo?.unpayed_sum) ?? 0
      const overageSum = Math.max(0, unpayed - remaining)
      return {
        agreement: Math.max(0, Math.min(1, agreementSum / total)),
        overage: Math.max(0, Math.min(1, overageSum / total))
      }
    },
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()
    return { showSucceedSnackbar, showErrorSnackbar }
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.tw-text-primary-light {
  color: $primary-light;
}
.pay-info-bar {
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 9999px;
  overflow: hidden;
  display: flex;
  gap: 5px;
}

.pay-info-bar__paid {
  height: 100%;
  background: #16a34a; // green-600
  border-radius: 9999px;
}

.pay-info-bar__remaining {
  height: 100%;
  background: #dc2626; // red-600
  border-radius: 9999px;
}

.pay-info-bar__overage {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 10px;
  background: #f97316; // orange-500
  border-top-right-radius: 9999px;
  border-bottom-right-radius: 9999px;
}
.info-title {
  color: #64748B;
}
.info-text {
  font-size: 12px;
  font-weight: 500;
}

.payments__filters {
  margin-bottom: 25px;
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.payments__filters-info {
  margin-left: auto;
}

.payment-notice-banner {
  :deep(.q-banner__content) {
    min-width: 0;
  }
}

.finance-payment-nested .nested-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.finance-payment-nested .nested-table th,
.finance-payment-nested .nested-table td {
  padding: 8px 12px;
  border: 1px solid #e6e8ef;
}

.finance-payment-nested .nested-table th {
  background: #f5f5f5;
  font-weight: 500;
}

.finance-payment-nested .nested-table tbody tr:nth-child(even) {
  background: #fafafa;
}

.expand-cell {
  width: 48px;
}
.checkbox {
  :deep(.q-checkbox__bg) {
    border-width: 1px !important;
  }
}

.subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;
  margin-top: 7px;
}

.nested-status-cell {
  padding: 10px;
  min-width: 300px;
  max-width: 330px;
}

.nested-status-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.nested-status-text {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #212121;
}

.nested-status-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;
  color: #64748b;
  margin-top: 7px;
  text-align: left;
  line-height: 1.6;
}

.nested-exceed-block {
  min-width: 280px;
}

.nested-pay-info-bar {
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 9999px;
  overflow: hidden;
  display: flex;
  gap: 5px;
}

.nested-pay-info-bar__paid {
  height: 100%;
  background: #16a34a;
  border-radius: 9999px;
}

.nested-pay-info-bar__remaining {
  height: 100%;
  background: #dc2626;
  border-radius: 9999px;
}

.nested-error-bi-text {
  font-size: 12px;
  color: #dc2626;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 360px;
  font-weight: 500;
}

.nested-docs-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 12px 0;
}

.nested-docs-row {
  display: grid;
  grid-template-columns: 150px 205px 88px;
  align-items: center;
  gap: 12px;
}

.nested-docs-status-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f97316;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
